import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import UserShortProfile from "../../components/userShortProfile/UserShortProfile";
import DefaultProfileImage from "../../medias/images/default.jpg";
import { getUserInformations } from "../../services/wisdomServices/userService";
import { requestGetImagesFromBucket } from "../../services/wisdomServices/trackingService";
import { getBlockers, updateBlocker, deleteBlockers } from "../../services/wisdomServices/growthService";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { getTeamMembers } from "../../services/wisdomServices/managerService";
import { getSessionItem } from "../../utils/helper/session";
import "./UserBlockers.css";
const MAX_LENGTH = 255;
const formatStatus = (status = "") => {
  return String(status)
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
const formatShortDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};
const getSeverityClass = (severity = "") => {
  switch (String(severity).toLowerCase()) {
    case "low":
      return "user-blockers-severity-low";
    case "medium":
      return "user-blockers-severity-medium";
    case "high":
      return "user-blockers-severity-high";
    case "urgent":
      return "user-blockers-severity-urgent";
    default:
      return "";
  }
};
function UserBlockers() {

  const { employeeId } = useParams();

  const loggedUser = getSessionItem("User");

  const ownerSearchRef = useRef(null);

  const [ownerSuggestions, setOwnerSuggestions] = useState([]);

  const [selectedStatus, setSelectedStatus] = useState("open");

  const statusOptions = [
      { label: "Open", value: "open" },
      { label: "In Progress", value: "in-progress" },
      { label: "Closed", value: "closed" }
  ];

  const [user, setUser] = useState(null);

  const [blockers, setBlockers] = useState([]);

  const [loadingUser, setLoadingUser] = useState(true);

  const [loadingBlockers, setLoadingBlockers] = useState(true);

  const [editingBlocker, setEditingBlocker] = useState(null);

  const [editForm, setEditForm] = useState({
      title: "",
      description: "",
      severity: "medium",
      ownerId: null,
      ownerName: "",
      status: "open"
  });

  const [savingBlocker, setSavingBlocker] = useState(false);

  const [deletingBlockerId, setDeletingBlockerId] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    loadUser();
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    loadBlockers();
  }, [employeeId, selectedStatus]);

  useEffect(() => () => { if (ownerSearchRef.current) clearTimeout(ownerSearchRef.current); }, []);

  const loadUser = async () => {
      try {
      setLoadingUser(true);
      const response = await getUserInformations(employeeId);
      if (!response) {
          setUser(null);
          return;
      }
      const userData = { ...response };
      if (!userData.image) {
          userData.image = DefaultProfileImage;
      } else {
          try {
          const blob = await requestGetImagesFromBucket(
              userData.id,
              userData.image,
              "profile"
          );
          userData.image = blob
              ? URL.createObjectURL(blob)
              : DefaultProfileImage;
          } catch (error) {
          console.error("Error loading profile image:", error);
          userData.image = DefaultProfileImage;
          }
      }
      setUser(userData);
      } catch (error) {
      console.error("Error loading user:", error);
      setUser(null);
      } finally {
      setLoadingUser(false);
      }
  };

  const loadBlockers = async () => {
    try {
        setLoadingBlockers(true);
        const params = { createdBy: employeeId, status: selectedStatus };
        const response = await getBlockers(params);
        setBlockers(response.data ?? []);
    } catch (error) {
        console.error("Error loading blockers:", error);
        console.error("API response:", error?.response?.data);
        setBlockers([]);
    } finally {
        setLoadingBlockers(false);
    }
  };

  const handleEditBlocker = (blocker) => {
      setEditingBlocker(blocker);
      setEditForm({
          title: blocker.title || "",
          description: blocker.description || "",
          severity: blocker.severity || "medium",
          ownerId: blocker.ownerId ?? null,
          ownerName: blocker.ownerName ?? "",
          status: blocker.status || "open"
      });
  };

  const handleCloseEdit = () => {
      if (savingBlocker) return;
      setEditingBlocker(null);
      setEditForm({
          title: "",
          description: "",
          severity: "medium",
          ownerId: null,
          ownerName: "",
          status: "open",
      });
      setOwnerSuggestions([]);
      if (ownerSearchRef.current) clearTimeout(ownerSearchRef.current);
  };

  const handleEditFieldChange = (field, value) => {
      setEditForm((previous) => ({
          ...previous,
          [field]: value,
      }));
      };
      const searchOwners = (event) => {
    if (ownerSearchRef.current) clearTimeout(ownerSearchRef.current);
    const query = event.query?.trim() ?? "";
    if (!query) { setOwnerSuggestions([]); return; }
    ownerSearchRef.current = setTimeout(async () => {
      try {
        const response = await getTeamMembers(loggedUser.id, false, query, 1, 100);
        setOwnerSuggestions((response?.data ?? []).map((person) => ({ id: person.id, name: person.fullName })));
      } catch (error) {
        console.error("Error searching blocker owners:", error);
        setOwnerSuggestions([]);
      }
    }, 500);
  };

  const handleOwnerChange = (value) => {
    if (value && typeof value === "object") {
      setEditForm((previous) => ({ ...previous, ownerId: value.id, ownerName: value.name }));
    } else {
      setEditForm((previous) => ({ ...previous, ownerId: null, ownerName: value || "" }));
    }
  };

  const handleSaveBlocker = async (event) => {
      event.preventDefault();
      if (!editingBlocker?.id) return;
      if (!editForm.title.trim() || !editForm.ownerId) {
          return;
      }
      try {
          setSavingBlocker(true);
          const payload = {
          title: editForm.title.trim(),
          description: editForm.description.trim(),
          severity: editForm.severity,
          ownerId: Number(editForm.ownerId),
          status: editForm.status
          };
          const response = await updateBlocker(
          editingBlocker.id,
          payload
          );
          setBlockers((previous) =>
          previous.map((blocker) =>
              blocker.id === editingBlocker.id
              ? {
                  ...blocker,
                  ...(response ?? {}),
                  ...payload,
                  ownerName: editForm.ownerName,
                  }
              : blocker
          )
          );
          setEditingBlocker(null);
          setOwnerSuggestions([]);
          await loadBlockers();
      } catch (error) {
          console.error("Error updating blocker:", error);
          console.error("API response:", error?.response?.data);
      } finally {
          setSavingBlocker(false);
      }
  };

  const handleDeleteBlocker = (blocker) => {
      if (!blocker?.id) return;
      confirmDialog({
          message: `Are you sure you want to delete "${blocker.title}"?`,
          header: "Delete blocker",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Delete",
          rejectLabel: "Cancel",
          acceptClassName: "p-button-danger",
          accept: async () => {
          try {
              setDeletingBlockerId(blocker.id);
              await deleteBlockers(blocker.id);
              setBlockers((previous) =>
              previous.filter((item) => item.id !== blocker.id)
              );
          } catch (error) {
              console.error("Error deleting blocker:", error);
              console.error("API response:", error?.response?.data);
          } finally {
              setDeletingBlockerId(null);
          }
          },
      });
  };
  return (
    <>
        {!loadingUser && user && (
        <div className="my-dashboard-header">
            <UserShortProfile
            employeeName={`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
            employeePronoun={user.pronoun}
            employeeDesignation={user.jobDescription}
            employeeID={user.id}
            employeeUsername={user.username}
            employeeLocation={user.city}
            employeePicture={user.image}
            employeeDisabled={user.isDisabled}
            employeeContractEndDate={user.contractEndDate}
            employeeDepartment={user.department}
            employeeIdvStatus={user.trolleyIdvStatus}
            employeePersonalEmail={user.personalEmail}
            showManagerLinks
            />
        </div>
        )}
        <div className="user-blockers-page">
          <div className="user-blockers-header">
              <div>
                  <div className="user-blockers-title-row">
                      <h1>Blockers</h1>
                      <span className="user-blockers-total-badge">
                          {blockers.length} {blockers.length === 1 ? "item" : "items"}
                      </span>
                  </div>
                  <p className="user-blockers-description">
                      Review blockers associated with this employee and monitor their SLA.
                  </p>
              </div>
              <div className="user-blockers-status-filter">
                <label htmlFor="blockers-status">Status:</label>
                <Dropdown
                    inputId="blockers-status"
                    value={selectedStatus}
                    options={statusOptions}
                    onChange={(e) => setSelectedStatus(e.value)}
                    placeholder="Select status"
                    className="user-blockers-status-dropdown"
                />
              </div>
          </div>
          {loadingBlockers ? (
            <div className="user-blockers-empty">
              <Loader2
                size={20}
                className="user-blockers-spinner"
              />
              Loading blockers...
            </div>
          ) : blockers.length === 0 ? (
            <div className="user-blockers-empty">
              No blockers found.
            </div>
          ) : (
            <div className="user-blockers-grid">
              {blockers.map((blocker, index) => {
                const key =
                  blocker.id ??
                  blocker.blockerId ??
                  `${blocker.title ?? "blocker"}-${index}`;
                return (
                  <article
                    key={key}
                    className="user-blockers-card"
                  >
                    <div className="user-blockers-card-top">
                      <div className="user-blockers-heading">
                        <h2>
                          {blocker.title || "Untitled blocker"}
                        </h2>
                        <div className="user-blockers-badge-row">
                          {blocker.severity && (
                            <span
                              className={`user-blockers-severity-badge ${getSeverityClass(
                                blocker.severity
                              )}`}
                            >
                              {formatStatus(blocker.severity)}
                            </span>
                          )}
                          {blocker.ownerName && (
                            <span className="user-blockers-owner-badge">
                              <i className="pi pi-user" />
                              {blocker.ownerName}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="user-blockers-card-actions">
                        <button
                          type="button"
                          className="user-blockers-edit-button"
                          onClick={() => handleEditBlocker(blocker)}
                        >
                          <i className="pi pi-pencil" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="user-blockers-delete-button"
                          disabled={deletingBlockerId === blocker.id}
                          onClick={() => handleDeleteBlocker(blocker)}
                        >
                          {deletingBlockerId === blocker.id ? (
                            <Loader2
                              size={14}
                              className="user-blockers-spinner"
                            />
                          ) : (
                            <i className="pi pi-trash" />
                          )}
                          Delete
                        </button>
                      </div>
                    </div>
                    <p className="user-blockers-card-description">
                      {blocker.description || "No description provided."}
                    </p>
                    <div className="user-blockers-card-footer">
                      <div className="user-blockers-meta">
                        <span>
                          <strong>Created by</strong>
                          {blocker.createdByName || "-"}
                        </span>
                        <span>
                          <strong>Created</strong>
                          {formatShortDate(blocker.createdAt)}
                        </span>
                        {blocker.updatedByName && (
                          <span>
                            <strong>Updated by</strong>
                            {blocker.updatedByName}
                          </span>
                        )}
                      </div>
                      {blocker.sla && (
                        <div className="user-blockers-sla-box">
                          <i className="pi pi-clock" />
                          <div>
                            <span>SLA</span>
                            <strong>
                              {formatDateTime(blocker.sla)}
                            </strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
        {editingBlocker && (
        <div
            className="user-blockers-modal-overlay"
            onMouseDown={handleCloseEdit}
        >
            <div
            className="user-blockers-modal"
            onMouseDown={(event) => event.stopPropagation()}
            >
            <div className="user-blockers-modal-header">
                <div>
                <h2>Edit blocker</h2>
                <p>
                    Update the blocker information below.
                </p>
                </div>
                <button
                type="button"
                className="user-blockers-modal-close"
                onClick={handleCloseEdit}
                disabled={savingBlocker}
                >
                <i className="pi pi-times" />
                </button>
            </div>
            <form
                className="user-blockers-modal-form"
                onSubmit={handleSaveBlocker}
            >
                <div className="user-blockers-form-group">
                  <label htmlFor="blocker-title">
                      Title
                  </label>
                  <input
                      id="blocker-title"
                      type="text"
                      value={editForm.title}
                      readOnly
                      disabled
                  />
                </div>
                <div className="user-blockers-form-row">
                    <div className="user-blockers-form-group">
                        <label htmlFor="blocker-severity">Severity</label>
                        <Dropdown
                            inputId="blocker-severity"
                            value={editForm.severity}
                            options={[
                                { label: "Low", value: "low" },
                                { label: "Medium", value: "medium" },
                                { label: "High", value: "high" },
                                { label: "Urgent", value: "urgent" }
                            ]}
                            onChange={(e) => handleEditFieldChange("severity", e.value)}
                            className="w-full"
                        />
                    </div>
                    <div className="user-blockers-form-group">
                        <label htmlFor="blocker-status">Status</label>
                        <Dropdown
                            inputId="blocker-status"
                            value={editForm.status}
                            options={[
                                { label: "Open", value: "open" },
                                { label: "In Progress", value: "in-progress" },
                                { label: "Closed", value: "closed" }
                            ]}
                            onChange={(e) => handleEditFieldChange("status", e.value)}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="user-blockers-form-group">
                  <label htmlFor="blocker-owner">Owner</label>
                  <AutoComplete
                    inputId="blocker-owner"
                    value={editForm.ownerName}
                    suggestions={ownerSuggestions}
                    completeMethod={searchOwners}
                    field="name"
                    forceSelection
                    appendTo="self"
                    delay={0}
                    placeholder="Search user..."
                    className="w-full"
                    inputClassName="w-full"
                    onChange={(event) => handleOwnerChange(event.value)}
                    onSelect={(event) => handleOwnerChange(event.value)}
                  />
                </div>
                <div className="user-blockers-form-group">
                  <label htmlFor="blocker-description">
                      Description
                  </label>
                  <textarea
                      id="blocker-description"
                      rows={4}
                      maxLength={255}
                      value={editForm.description}
                      onChange={(event) =>
                      handleEditFieldChange(
                          "description",
                          event.target.value
                      )
                      }
                  />
                </div>
                <div className="user-blockers-modal-footer">
                  <button
                      type="button"
                      className="user-blockers-cancel-button"
                      onClick={handleCloseEdit}
                      disabled={savingBlocker}
                  >
                      Cancel
                  </button>
                  <button
                      type="submit"
                      className="user-blockers-save-button"
                      disabled={savingBlocker || !editForm.ownerId}
                  >
                      {savingBlocker && (
                      <Loader2
                          size={15}
                          className="user-blockers-spinner"
                      />
                      )}
                      Save changes
                  </button>
                </div>
            </form>
            </div>
        </div>
        )}
    </>
  );
}
export default UserBlockers;
